import React, { useState, useEffect } from 'react';
import * as ReactDOM from 'react-dom';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { Button, Modal } from 'common/lazy';
import { SVGIcon as Icon } from 'common/components';
import { useClassName, useWindowSize } from 'common/hooks';
import { LazyDayPicker } from 'common/lazy';
import PriceTypeIndicator from '../PriceTypeIndicator/PriceTypeIndicator';
import { DateSchedulePanel } from '../DateSchedule';
import CloseIcon from 'common/assets/checkout-close.svg';
import {
  CALENDAR_DATE_STATUS,
  getCalendarPriceLabel,
} from 'utils/calendarPricing';

import './modal-calendar.less';

const EmptyElement = () => null;
const WEEKDAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const getValidDate = (date) => {
  if (!date) return null;

  const parsedDate = DateTime.fromISO(date);
  return parsedDate.isValid ? parsedDate : null;
};

const getVisibleDate = (initialVisibleDate, initialDate) =>
  getValidDate(initialVisibleDate) ||
  getValidDate(initialDate) ||
  DateTime.local();

const ModalCalendar = ({
  initialDate,
  open,
  updateSelectedDate,
  closeModal,
  isDateUnavailable,
  getDateStatus,
  trigger,
  openModal,
  footer,
  isTicketsCalendar,
  priceTypes,
  regularPriceTypes,
  datePricing,
  legendTypes,
  venueName,
  venueId,
  ridesCards,
  openingHours,
  characterAppearancesTitle,
  maxDate,
  initialVisibleDate,
  requireExplicitSelection,
}) => {
  const className = useClassName('ModalCalendar');
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(() => {
    const committedDate = getValidDate(initialDate);
    if (committedDate) return committedDate.toJSDate();
    return requireExplicitSelection ? null : DateTime.local().toJSDate();
  });
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(
    () => getVisibleDate(initialVisibleDate, initialDate).month
  );

  // Tickets variant: controlled month for the custom prev/next navigation.
  const [visibleMonth, setVisibleMonth] = useState(() =>
    getVisibleDate(initialVisibleDate, initialDate).startOf('month').toJSDate()
  );

  const { isMobile } = useWindowSize();
  const today = new Date();

  useEffect(() => {
    const committedDate = getValidDate(initialDate);
    if (committedDate) {
      setCalendarSelectedDate(committedDate.toJSDate());
      return;
    }
    if (requireExplicitSelection) setCalendarSelectedDate(null);
  }, [initialDate, requireExplicitSelection]);

  useEffect(() => {
    const nextDate = getVisibleDate(initialVisibleDate, initialDate);
    setCurrentCalendarMonth(nextDate.month);
    setVisibleMonth(nextDate.startOf('month').toJSDate());
  }, [initialDate, initialVisibleDate]);

  useEffect(() => {
    if (!open || !requireExplicitSelection) return;

    const committedDate = getValidDate(initialDate);
    const nextVisibleDate = getVisibleDate(initialVisibleDate, initialDate);
    setCalendarSelectedDate(committedDate ? committedDate.toJSDate() : null);
    setCurrentCalendarMonth(nextVisibleDate.month);
    setVisibleMonth(nextVisibleDate.startOf('month').toJSDate());
  }, [open, initialDate, initialVisibleDate, requireExplicitSelection]);

  useEffect(() => {
    if (!open) return;
    // Tickets variant colors each cell via renderDay (no DOM injection).
    if (isTicketsCalendar) return;
    if (priceTypes?.length) renderPriceTypeIndicator();
    const date = calendarSelectedDate
      ? DateTime.fromJSDate(calendarSelectedDate)
      : DateTime.local();
    defineCustomStyles(date);
  }, [open]);

  useEffect(() => {
    if (isTicketsCalendar) return;
    const date = calendarSelectedDate
      ? DateTime.fromJSDate(calendarSelectedDate)
      : DateTime.local();
    defineCustomStyles(date);
  }, [calendarSelectedDate]);

  const isDateBeforeToday = (date) => {
    return new Date(date.toDateString()) < new Date(new Date().toDateString());
  };

  const toISO = (jsDate) => DateTime.fromJSDate(jsDate).toISODate();

  const getDatePricing = (date) => datePricing?.[toISO(date)];

  const resolveDateStatus = (date) => getDateStatus(toISO(date));

  const isDateSoldOut = (date) =>
    resolveDateStatus(date) === CALENDAR_DATE_STATUS.SOLD_OUT;

  // Cap beyond the pre-computed pricing window (see checkout const.js): dates
  // past maxDate have no sweep data, so they are disabled/unselectable.
  const isDateAfterMax = (date) =>
    Boolean(
      maxDate &&
        DateTime.fromJSDate(date).startOf('day') >
          DateTime.fromJSDate(maxDate).startOf('day')
    );

  const isCurrentMonth = (today = DateTime.local()) =>
    today.month === currentCalendarMonth ? 'current-calendar-month' : '';

  const onDayClick = (date) => {
    if (
      isDateBeforeToday(date) ||
      isDateUnavailable(toISO(date)) ||
      isDateSoldOut(date) ||
      isDateAfterMax(date)
    ) {
      return;
    }

    setCalendarSelectedDate(date);
  };

  const defineCustomStyles = (date) => {
    if (!date?.isValid) return;
    setCurrentCalendarMonth(date.month);

    const today = DateTime.local().startOf('day');

    const monthDays = [];

    // First add regular weekday price types
    if (regularPriceTypes) {
      let currentDate = DateTime.local(date.year, date.month, 1);
      const endDate = currentDate.endOf('month');

      while (currentDate <= endDate) {
        if (currentDate >= today) {
          const priceType =
            regularPriceTypes[currentDate.weekdayLong.toLowerCase()];

          if (priceType) {
            monthDays.push({
              day: currentDate.day,
              type: priceType,
            });
          }
        }

        currentDate = currentDate.plus({ days: 1 });
      }
    }

    // Then override with custom date price types
    priceTypes?.forEach((priceType) => {
      const priceTypeDate = DateTime.fromISO(priceType.date).startOf('day');

      if (
        priceTypeDate >= today &&
        priceTypeDate.month === date.month &&
        priceTypeDate.year === date.year
      ) {
        const existingIndex = monthDays.findIndex(
          (day) => day.day === priceTypeDate.day
        );

        if (existingIndex >= 0) {
          monthDays[existingIndex].type = priceType.type;
        } else {
          monthDays.push({
            day: priceTypeDate.day,
            type: priceType.type,
          });
        }
      }
    });

    if (datePricing) {
      Object.entries(datePricing).forEach(([isoDate, pricing]) => {
        const priceDate = DateTime.fromISO(isoDate).startOf('day');

        if (
          priceDate >= today &&
          priceDate.month === date.month &&
          priceDate.year === date.year &&
          pricing?.tier
        ) {
          const existingIndex = monthDays.findIndex(
            (day) => day.day === priceDate.day
          );
          const status = getDateStatus(isoDate);
          const type =
            status === CALENDAR_DATE_STATUS.CLOSED ||
            status === CALENDAR_DATE_STATUS.SOLD_OUT
              ? status
              : pricing.tier;

          if (existingIndex >= 0) {
            monthDays[existingIndex].type = type;
          } else {
            monthDays.push({
              day: priceDate.day,
              type,
            });
          }
        }
      });
    }

    const calendarDays = document.querySelectorAll('.DayPicker-Day');

    calendarDays.forEach((calendarDay) => {
      calendarDay.classList.remove(
        className('price-type-value'),
        className('price-type-standard'),
        className('price-type-peak'),
        className('price-type-non-peak'),
        className('price-type-promo'),
        className('price-type-closed'),
        className('price-type-sold-out')
      );

      const priceType = monthDays.find(
        (day) => day.day === Number(calendarDay.innerText)
      );

      if (!priceType) return;

      const newClass = className(`price-type-${priceType.type}`);

      if (!calendarDay.classList.contains(newClass)) {
        calendarDay.classList.add(newClass);
      }
    });
  };

  const renderPriceTypeIndicator = () => {
    const datePickerCaption = document.querySelector('.DayPicker-Caption');
    if (!datePickerCaption) return;
    if (
      datePickerCaption.querySelector(
        `.${className('price-indicator-container')}`
      )
    ) {
      return;
    }
    const priceTypeIndicator = document.createElement('div');
    priceTypeIndicator.className = className('price-indicator-container');
    datePickerCaption.appendChild(priceTypeIndicator);
    ReactDOM.render(<PriceTypeIndicator />, priceTypeIndicator);
  };

  const renderApplyButton = () => (
    <Button inverted onClick={() => updateSelectedDate(calendarSelectedDate)}>
      Apply
    </Button>
  );

  const renderPicker = () => (
    <LazyDayPicker
      className={`day-picker ${isCurrentMonth()}`}
      disabledDays={[
        { before: today },
        ...(maxDate ? [{ after: maxDate }] : []),
        (jsDate) => isDateUnavailable(toISO(jsDate)),
        (jsDate) => isDateSoldOut(jsDate),
      ]}
      enableOutsideDaysClick={false}
      weekdaysShort={WEEKDAYS_SHORT}
      selectedDays={calendarSelectedDate}
      month={calendarSelectedDate || today}
      onDayClick={(date) => onDayClick(date)}
      onMonthChange={(date) => defineCustomStyles(DateTime.fromJSDate(date))}
    />
  );

  /* ----------------------------- Tickets variant ----------------------------- */

  const isPrevMonthDisabled =
    DateTime.fromJSDate(visibleMonth).startOf('month') <=
    DateTime.local().startOf('month');

  // No point navigating past the month that holds maxDate: everything there is
  // disabled (outside the pricing window).
  const isNextMonthDisabled = Boolean(
    maxDate &&
      DateTime.fromJSDate(visibleMonth).startOf('month') >=
        DateTime.fromJSDate(maxDate).startOf('month')
  );

  const goToMonth = (delta) => {
    if (delta < 0 && isPrevMonthDisabled) return;
    if (delta > 0 && isNextMonthDisabled) return;
    setVisibleMonth(
      DateTime.fromJSDate(visibleMonth).plus({ months: delta }).toJSDate()
    );
  };

  const renderTicketsDay = (day, modifiers) => {
    const pricing = getDatePricing(day);
    const priceLabel = getCalendarPriceLabel(pricing);
    const status = resolveDateStatus(day);
    const statusLabel =
      status === CALENDAR_DATE_STATUS.CLOSED ? 'CLOSED' : null;
    const tier =
      status === CALENDAR_DATE_STATUS.CLOSED ||
      status === CALENDAR_DATE_STATUS.SOLD_OUT
        ? status
        : pricing?.tier;
    const cellClass = [
      className('day-cell'),
      tier ? className(`day-tier-${tier}`) : '',
      modifiers.today ? 'is-today' : '',
      modifiers.selected ? 'is-selected' : '',
      modifiers.outside ? 'is-outside' : '',
      modifiers.disabled ? 'is-disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={cellClass}>
        <span className="day-num">{day.getDate()}</span>
        {!modifiers.outside &&
        (statusLabel || (!modifiers.disabled && priceLabel)) ? (
          <span className="day-price">{statusLabel || priceLabel}</span>
        ) : (
          <span className="day-price"></span>
        )}
      </div>
    );
  };

  const renderTicketsPicker = () => (
    <LazyDayPicker
      className="day-picker"
      month={visibleMonth}
      captionElement={EmptyElement}
      navbarElement={EmptyElement}
      renderDay={renderTicketsDay}
      disabledDays={[
        { before: today },
        ...(maxDate ? [{ after: maxDate }] : []),
        (jsDate) => isDateUnavailable(toISO(jsDate)),
        (jsDate) => isDateSoldOut(jsDate),
      ]}
      enableOutsideDaysClick={false}
      weekdaysShort={WEEKDAYS_SHORT}
      selectedDays={calendarSelectedDate}
      onDayClick={(date) => onDayClick(date)}
    />
  );

  const renderTicketsDetails = (mobile = false) => {
    if (!calendarSelectedDate) return null;

    return (
      <div
        className={className([
          'details',
          mobile ? 'mobile-selected-date' : '',
        ])}>
        <DateSchedulePanel
          venueId={venueId}
          ridesCards={ridesCards}
          openingHours={openingHours}
          date={calendarSelectedDate}
          characterAppearancesTitle={characterAppearancesTitle}
          showAdditionalDetails={!mobile}
        />
      </div>
    );
  };

  const renderTicketsCalendar = () => (
    <div className={className('checkout-shell')}>
      <div className={className('checkout-header')}>
        <div>
          {venueName && <p className={className('eyebrow')}>{venueName}</p>}
          <h2>Pick a date</h2>
        </div>
        <button
          type="button"
          aria-label="Close calendar"
          className={className('close')}
          onClick={closeModal}>
          <img src={CloseIcon} alt="" />
        </button>
      </div>

      <div className={className('checkout-body')}>
        <div className={className('checkout-left')}>
          {Boolean(legendTypes?.length) && (
            <div className={className('checkout-legend')}>
              <PriceTypeIndicator types={legendTypes} variant="dynamic" />
            </div>
          )}

          <div className={className('month-nav')}>
            <button
              type="button"
              aria-label="Previous month"
              className={className([
                'month-nav-btn',
                isPrevMonthDisabled ? 'month-nav-btn-disabled' : '',
              ])}
              onClick={() => goToMonth(-1)}>
              <Icon name="angle-left" size="mini" />
            </button>
            <span className={className('month-nav-label')}>
              {DateTime.fromJSDate(visibleMonth).toFormat('MMMM yyyy')}
            </span>
            <button
              type="button"
              aria-label="Next month"
              className={className([
                'month-nav-btn',
                isNextMonthDisabled ? 'month-nav-btn-disabled' : '',
              ])}
              onClick={() => goToMonth(1)}>
              <Icon name="angle-right" size="mini" />
            </button>
          </div>

          <div className={className(['calendar', 'tickets-calendar'])}>
            {renderTicketsPicker()}
          </div>

          {isMobile && renderTicketsDetails(true)}

          <div className={className('checkout-actions')}>
            <Button
              disabled={!calendarSelectedDate}
              onClick={() => updateSelectedDate(calendarSelectedDate)}>
              Select
            </Button>
          </div>
        </div>

        {!isMobile && renderTicketsDetails()}
      </div>
      {footer}
    </div>
  );

  const renderRegularCalendar = () => (
    <div className={className(['calendar', 'regular-calendar'])}>
      {renderPicker()}
      {renderApplyButton()}
      {footer}
    </div>
  );

  const renderDayPicker = () =>
    isTicketsCalendar ? renderTicketsCalendar() : renderRegularCalendar();

  return (
    <Modal
      basic
      open={open}
      trigger={trigger}
      onOpen={openModal}
      size={isTicketsCalendar ? 'large' : 'small'}
      onClose={closeModal}
      className={className([
        'modal',
        isTicketsCalendar ? 'checkout-modal' : '',
      ])}>
      {renderDayPicker()}
    </Modal>
  );
};

ModalCalendar.propTypes = {
  initialDate: PropTypes.string,
  initialVisibleDate: PropTypes.string,
  requireExplicitSelection: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  isTicketsCalendar: PropTypes.bool,
  priceTypes: PropTypes.array,
  regularPriceTypes: PropTypes.object,
  datePricing: PropTypes.object,
  legendTypes: PropTypes.array,
  venueName: PropTypes.string,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ridesCards: PropTypes.array,
  openingHours: PropTypes.object,
  characterAppearancesTitle: PropTypes.string,
  maxDate: PropTypes.instanceOf(Date),
  updateSelectedDate: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  isDateUnavailable: PropTypes.func.isRequired,
  getDateStatus: PropTypes.func,
  trigger: PropTypes.any,
  footer: PropTypes.any,
  openModal: PropTypes.func,
};

ModalCalendar.defaultProps = {
  isDateUnavailable: () => false,
  getDateStatus: () => CALENDAR_DATE_STATUS.AVAILABLE,
  openModal: () => false,
  isTicketsCalendar: false,
  priceTypes: [],
  regularPriceTypes: null,
  datePricing: null,
  legendTypes: null,
  venueName: null,
  venueId: null,
  ridesCards: [],
  openingHours: null,
  characterAppearancesTitle: 'Character Meet & Greet',
  maxDate: null,
  initialVisibleDate: null,
  requireExplicitSelection: false,
};

export default ModalCalendar;
